import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/api";

const Login = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [credentials, setCredentials] = useState({ email: "", senha: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");

  // Redirecionar para agenda após login conforme especificado
  const from = location.state?.from?.pathname || "/agenda";

  useEffect(() => {
    if (isAuthenticated() && !loading) {
      // Usuário já está logado, redirecionar
    }
  }, [isAuthenticated, loading]);

  if (isAuthenticated() && !loading) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await login(credentials);

      if (!result.success) {
        setError(result.message || "Erro ao fazer login");
      }
      // Se sucesso, o redirect será automático via useEffect
    } catch (err) {
      setError("Erro inesperado ao fazer login");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenForgot = () => {
    setForgotOpen(true);
    setForgotEmail("");
    setForgotMsg("");
  };

  const handleCloseForgot = () => {
    setForgotOpen(false);
  };

  const handleSubmitForgot = async (e) => {
    e.preventDefault();
    setForgotMsg("");
    try {
      await authService.forgotPassword(forgotEmail);
      setForgotMsg(
        "se o email existir, enviaremos instruções para redefinir a senha",
      );
    } catch (err) {
      // resposta deve ser genérica mesmo em erro
      setForgotMsg(
        "se o email existir, enviaremos instruções para redefinir a senha",
      );
    }
  };

  // Mostrar loading se ainda estiver verificando autenticação
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, #2563eb 0%, #1e40af 25%, #3b82f6 50%, #60a5fa 75%, #93c5fd 100%)",
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #2563eb 0%, #1e40af 25%, #3b82f6 50%, #60a5fa 75%, #93c5fd 100%)",
        backgroundAttachment: "fixed",
        padding: 2,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src="/logo.png"
          alt="Logo"
          style={{
            maxWidth: "200px",
            maxHeight: "200px",
            filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.2))",
          }}
        />
      </Box>

      {/* Card de Login */}
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          mx: 2,
          backdropFilter: "blur(20px)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(37, 99, 235, 0.2)",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              textAlign: "center",
              fontWeight: 600,
              color: "#1e3a8a",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Olha o prazo!
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={credentials.email}
              onChange={handleChange}
              margin="normal"
              required
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label="Senha"
              name="senha"
              type="password"
              value={credentials.senha}
              onChange={handleChange}
              margin="normal"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%)",
                },
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Entrar"
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Link
              component="button"
              variant="body2"
              onClick={handleOpenForgot}
              sx={{ mt: 1 }}
            >
              Esqueci minha senha
            </Link>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={forgotOpen}
        onClose={handleCloseForgot}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Redefinir senha</DialogTitle>
        <DialogContent>
          {forgotMsg && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {forgotMsg}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmitForgot} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForgot}>Fechar</Button>
          <Button onClick={handleSubmitForgot} variant="contained">
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
